<script lang="ts">
	import Icon from '../Icon/Icon.svelte'
	import type { IconName } from '$lib_commons/svg-icons/iconsMap.ts'
	import { BadgeDot, BadgeDotSize, type BadgeDotType } from '../BadgeDot'
	import {
		STATUS_BADGE_TONE_CONFIG,
		StatusBadgeSize,
		StatusBadgeTone,
		StatusBadgeVariant,
		type StatusBadgeProperty
	} from './constants.ts'

	type Props = {
		label: string
		class?: string
		ariaLabel?: string
		textColor?: string
		tone?: StatusBadgeTone
		size?: StatusBadgeSize
		icon?: IconName | null
		dotType?: BadgeDotType
		backgroundColor?: string
		variant?: StatusBadgeVariant
		property?: StatusBadgeProperty
		spinning?: boolean
	}

	let {
		label,
		tone = StatusBadgeTone.NEUTRAL,
		size = StatusBadgeSize.MEDIUM,
		variant = StatusBadgeVariant.ICON,
		property,
		icon,
		dotType,
		backgroundColor,
		textColor,
		spinning = false,
		class: className = '',
		ariaLabel
	}: Props = $props()

	const toneConfig = $derived(STATUS_BADGE_TONE_CONFIG[tone])

	const resolvedVariant = $derived(property ?? variant)

	const resolvedIcon = $derived(icon === null ? undefined : (icon ?? toneConfig.icon))

	const badgeStyle = $derived(
		`--status-badge-bg:${backgroundColor ?? toneConfig.backgroundColor};--status-badge-color:${textColor ?? toneConfig.textColor}`
	)
</script>

<span
	class={`status-badge status-badge--${size} ${resolvedVariant === StatusBadgeVariant.INLINE ? 'status-badge--inline' : ''} ${className}`}
	style={badgeStyle}
	aria-label={ariaLabel}
	role={ariaLabel ? 'img' : undefined}
>
	{#if resolvedVariant === StatusBadgeVariant.ICON && resolvedIcon}
		<Icon
			name={resolvedIcon}
			size={size === StatusBadgeSize.SMALL ? 'sm' : 'md'}
			class={spinning ? 'animate-spin' : ''}
		/>
	{:else if resolvedVariant === StatusBadgeVariant.DOT}
		<span class="status-dot" aria-hidden="true"></span>
	{:else if resolvedVariant === StatusBadgeVariant.INLINE && dotType}
		<BadgeDot
			type={dotType}
			size={size === StatusBadgeSize.SMALL ? BadgeDotSize.MEDIUM : BadgeDotSize.LARGE}
			{spinning}
			{ariaLabel}
		/>
	{/if}

	<span class="status-label">{label}</span>
</span>

<style lang="postcss">
	.status-badge {
		background: var(--status-badge-bg);
		color: var(--status-badge-color);

		@apply inline-flex items-center gap-1.5 rounded-full whitespace-nowrap text-center;
	}

	.status-badge--small {
		@apply h-6 px-2 py-0.5 text-xs font-normal leading-4;
	}

	.status-badge--medium {
		@apply h-8 px-2 py-1 text-sm font-normal leading-5;
	}

	.status-badge--inline {
		@apply h-auto bg-transparent p-0 text-xs font-medium leading-4;
	}

	.status-label {
		@apply leading-none;
	}

	.status-dot {
		background: var(--status-badge-color);

		@apply h-1.5 w-1.5 rounded-full;
	}
</style>
